import React, {useEffect, useState} from "react";
import {
  AppointmentBookingServicePointList,
  AppointmentBookingServicePointListItemTemplateProps,
  AppointmentBookingServicePointListPagination,
  PossibleAppointment
} from "@/pages/AppointmentBooking/AppointmentBookingServicePointList/AppointmentBookingServicePointList";
import styles from "./SingleSpecialtyBookingWidget.module.scss";
import {Button, Icon, IconSize} from "@reapptor-apps/reapptor-react-components";
import {PillButtonGroup, PillButtonGroupPagination} from "@/components/PillButtonGroup/PillButtonGroup";
import GetAppointmentsInternalRequest from "@/models/server/requests/GetAppointmentsInternalRequest";
import {ApiProvider, ch, PageRouteProvider} from "@reapptor-apps/reapptor-react-common";
import {Utility} from "@reapptor-apps/reapptor-toolkit";
import Localizer from "@/localization/Localizer";
import moment from "moment-timezone";
import PageDefinitions from "@/providers/PageDefinitions";
import {DateTime} from "luxon";
import {Care} from "@/models/interfaces/ICare";
import _ from 'lodash';

interface Location {
  id: string;
  label: string;
  careIds: string[];
}

export interface SingleSpecialtyBookingWidgetProps {
  typeOfCareList: Care[];
  locationList: Location[];
  onReserveTimeClick: (appointment: PossibleAppointment) => void;
  selectedSpecialtyExternalId: string;
  cardTitle: string;
  iconName?: string;
  maxNumListedAppts?: number;
  showLocationPicker?: boolean;
}

// Porvoo, Porvoo Tori, Loviisa, Mäntsälä, Helsinki
const locationsOrder = ['1', '3', '4', '5', '2'];

const ListItemTemplate = (props: AppointmentBookingServicePointListItemTemplateProps) => {
  const {Action, AvailableTime, Profile, ProfileWithAptType, item, index, isLastItemInVisibleList, SpecialistImage, ProfileNoPic, WeekDay, TimeAndDuration, AvailableDate} = props;
  return (
      <div>
        {
            !ch.mobile && (
                <div className={styles.listItemContainer} key={index} data-is-last-item-in-visible-list={String(isLastItemInVisibleList)}>
                  <div>
                    {AvailableTime}
                    <div className={styles.duration}>{item.durationInMin} min</div>
                  </div>
                  <div>
                    {ProfileWithAptType}
                  </div>
                  {Action}
                </div>
            )
        }
        {
            ch.mobile && (
                <div className={styles.mobileListItemDiv} data-is-last-item-in-visible-list={String(isLastItemInVisibleList)}>
                  <div className={styles.profileDiv}>
                    {SpecialistImage}
                  </div>
                  <div className={styles.profileDiv2}>
                    {ProfileNoPic}
                  </div>
                  <div className={styles.mobileTimeDiv}>
                    <div className={styles.mobileTimeInnerDiv}>
                      <div className={styles.timeAndDurationDiv}>
                        {TimeAndDuration}
                      </div>
                      {AvailableDate}
                    </div>
                  </div>
                  <div className={styles.mobileButtonDiv}>
                    {Action}
                  </div>
                </div>
            )
        }
      </div>
  );
}
export const SingleSpecialtyBookingWidget = (props: SingleSpecialtyBookingWidgetProps) => {
  const [selectedDate, setSelectedDate] = useState<Date>(moment().toDate());
  const [selectedTypeOfCare, setSelectedTypeOfCare] = useState<Care | null>(props.typeOfCareList.filter(care => care.id == props.selectedSpecialtyExternalId)[0]);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [pagedLocationData, setPagedLocationData] = useState<PillButtonGroupPagination<Location>>({
    items: _.sortBy(props.locationList, (item) => _.indexOf(locationsOrder, item.id)).filter(({ id }) => 
        selectedTypeOfCare ? selectedTypeOfCare.locationIds.includes(id) : true),
    loading: false,
    pageIndex: 0,
    pageSize: 4,
    totalCount: 10
  });
  const [typeOfCarePagedData, setTypeOfCarePagedData] = useState<PillButtonGroupPagination<Care>>({
    items: props.typeOfCareList.filter(item => item.id == props.selectedSpecialtyExternalId),
    loading: false,
    pageIndex: 0,
    pageSize: 3,
    totalCount: 10
  });
  const [pagedPossibleAppointments, setPagedPossibleAppointments] = useState<AppointmentBookingServicePointListPagination<PossibleAppointment>>({
    loading: false,
    pageIndex: 0,
    pageSize: 2,
    totalCount: 25,
    items: []
  });

  useEffect(() => {
    const fetchData = async () => {
      setPagedPossibleAppointments({
        ...pagedPossibleAppointments,
        loading: true,
      })
      let {appointments, data} = await getAppointments(selectedLocation, selectedTypeOfCare, selectedDate);
      setPagedPossibleAppointments({
        ...data,
        loading: false,
      })
    }
    fetchData();

  }, [selectedDate, selectedLocation, selectedTypeOfCare])


  async function getAppointments(location: Location | null = null, typeOfCare: Care | null = null, date:Date) {
    const getDate = (hours = 0, min = 0) => {
      return DateTime.fromJSDate(date).set({ hour: hours, minute: min, second: 0, millisecond: 0 }).toUTC(0,{keepLocalTime: true}).toJSDate();
    }

    const request: GetAppointmentsInternalRequest = {
      dateRangeStart: getDate(0, 0),
      dateRangeEnd: getDate(0, 0),
      timeRangeStart: getDate(5, 0),
      timeRangeEnd: getDate(18, 0),
      selectedService: typeOfCare ? [typeOfCare.id] : [],
      selectedPractitioners: typeOfCare ? typeOfCare.practitionerIds : [],
      selectedClinics: location ? [location.id] : selectedLocation ? [selectedLocation.id] : [],
      occupationalHealth: false,
    };

    const appointments: PossibleAppointment[] = await ApiProvider.postAsync("/api/Application/GetAvailableAppointments", request);

    let data: AppointmentBookingServicePointListPagination<PossibleAppointment> = {
      items: appointments,
      loading: false,
      pageIndex: 0,
      pageSize: props.maxNumListedAppts ?? 5,
      totalCount: appointments.length
    };
    return {appointments, data};
  }

  return (
      <div className={styles.container}>
        <div className={styles.cardHeader}>
          <div>
            {props.iconName ? (<Icon name={props.iconName} style={1} size={IconSize.Normal}/>) : null}
            <span>{props.cardTitle}</span>
          </div>
          <div>{Localizer.appointmentPageInstantlyFreeTimes}</div>
        </div>
        <div className={styles.pillButtonGroup}>
          <div>{Localizer.appointmentPageSpecialty}</div>
          <PillButtonGroup<Care>
              pagedData={typeOfCarePagedData}
              label={(item) => item.label}
              isSelected={(item) => selectedTypeOfCare ? selectedTypeOfCare.id === item.id : false}
              onClick={async (item: Care) => {
                setSelectedTypeOfCare(item)
              }}
              onShowMoreClick={() => {
                setTypeOfCarePagedData({
                  ...typeOfCarePagedData,
                  loading: true
                });
                //  Simulate data fetching.
                setTimeout(() => {
                  setTypeOfCarePagedData({
                    ...typeOfCarePagedData,
                    loading: false,
                    pageSize: 2000,
                    pageIndex: typeOfCarePagedData.pageIndex + 1,
                    items: [...typeOfCarePagedData.items]
                  });
                }, 20);
              }}
          />
        </div>
        {props.showLocationPicker ? (
            <div className={styles.pillButtonGroup}>
              <div>{Localizer.appointmentPageLocation}</div>
              <PillButtonGroup<Location>
                  pagedData={pagedLocationData}
                  label={(item) => item.label}
                  isSelected={(item) => selectedLocation ? selectedLocation.id === item.id : false}
                  onClick={async (item: Location) => {
                    setSelectedLocation(item)
                  }}
                  onShowMoreClick={() => {
                    setPagedLocationData({
                      ...pagedLocationData,
                      loading: true
                    });
                    //  Simulate data fetching.
                    setTimeout(() => {
                      setPagedLocationData({
                        ...pagedLocationData,
                        loading: false,
                        pageSize: 2000,
                        pageIndex: pagedLocationData.pageIndex + 1,
                        items: [...pagedLocationData.items]
                      });
                    }, 20);
                  }}
              />
            </div>
        ) : null}
        <div className={styles.date}>
          <Button icon={{name: 'chevron-left', style: 2, size: IconSize.Normal}}
                  disabled={selectedDate < new Date()}
                  onClick={
                    async () => {
                      let newDate = moment(selectedDate).add(-1, 'days').toDate();
                      await setSelectedDate(newDate)
                    }
                  }
          />

          <span className={styles.dateText}>{selectedDate.toLocaleString(ch.language, {weekday: 'long'})} {Utility.format("{0:dd.MM}", selectedDate)}</span>
          <Button icon={{name: 'chevron-right', style: 2, size: IconSize.Normal}}
                  onClick={
                    async () => {
                      let newDate = moment(selectedDate).add(1, 'days').toDate();
                      await setSelectedDate(newDate)
                    }
                  }
          />
        </div>
        <AppointmentBookingServicePointList
            ListItemTemplate={ListItemTemplate}
            pagedData={pagedPossibleAppointments}
            onShowMoreClick={async () => {
              await PageRouteProvider.redirectAsync(PageDefinitions.appointmentBooking.route(
                  {
                    params: {
                      selectedDate: moment(selectedDate).utcOffset(6, true).toDate().toISODateString(),
                      selectedLocation: selectedLocation ? selectedLocation.id : undefined,
                      occupationalHealth: (selectedTypeOfCare && selectedTypeOfCare.id == 'TTL') ? 'true' : 'false',
                      ...(selectedTypeOfCare && {selectedCare: selectedTypeOfCare.id})
                    }
                  }));
            }}
            onReserveTimeClick={(appointment: PossibleAppointment) => {
              props.onReserveTimeClick(appointment)
            }}
        />
      </div>
  );
}
